/**
 * SearchScopeFilter — the palette's filter row.
 *
 * Filters by KIND OF ANSWER, which is the distinction the palette actually
 * has: your captures, or a command (a page to go to, a setting to open). It
 * replaced a row of entity-type chips (Collections / Notes / Attachments /
 * Protocols / Equipment), which filtered only the content zone and therefore
 * could not express "just show me commands" at all — and cost five chips of
 * width to say something most searches don't need.
 *
 * Workspace is a chip like the others (tap it: your stuff, every type) with a
 * chevron that opens its type checkboxes. Opening the menu returns to the
 * Workspace scope before it applies a type, so it always narrows results, and
 * the chip's label says what is selected ("Workspace · Notes"). It used to be a
 * menu trigger only, labelled by the selection ("6 types"), so it neither
 * looked nor behaved like its neighbours. The menu also gives the palette
 * its first way to BROWSE by type: pick Protocols with an empty query and you
 * get your protocols. Searching the word "protocol" never could do that —
 * full-text search covers title, markdown and tags, and an artifact's TYPE is
 * not indexed, so a protocol called "Buffer Preparation (PBS)" correctly
 * matched nothing.
 *
 * The Help scope searches the help centre's real article corpus, so it DOES
 * find a phrase that appears only in an article's body. The index is built
 * in-process from the published corpus (`services/infra/help-index.service.ts`)
 * and the query never leaves Bower, so every workspace — PHI included — gets
 * the same answers. The curated `HELP_LINKS` pages are the fallback, shown
 * while the index is still building or if it could not be built.
 */

import { useEffect, useRef } from 'react';
import {
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@chakra-ui/react';

import { MaterialIcon } from '../../ui/MaterialIcon';
import { ENTITY_ICONS } from '../../../theme/entityIcons';
import type { EntityType as ThemeEntityType } from '../../../theme/entityIcons';
import type { EntityType } from '../../../services/search.service';
import { useSpreadsheetsEnabled } from '../../../hooks/data/useSpreadsheetsEnabled';

/** Which kind of answer the palette should show. */
export type SearchScope = 'all' | 'workspace' | 'chats' | 'navigation' | 'settings' | 'help';

/**
 * "Workspace", not "Captures": the zone holds notes, protocols, attachments,
 * equipment and collections — most of which nobody calls a capture. It is
 * simply your stuff, as opposed to the app's own commands.
 *
 * Navigation / Settings / Help are separate rather than one "Commands" scope
 * because they answer different questions ("take me somewhere", "change
 * something", "explain something") and the registry already groups them that
 * way — collapsing them lost that for no gain.
 */
const SCOPES: ReadonlyArray<{ value: SearchScope; label: string }> = [
  // "All" is first and is the default, so an unfiltered palette is a VISIBLE,
  // clickable state rather than a side effect of hiding the filter row.
  //
  // Before this existed, `showAllResultZones` was `expanded && !showFilterRow`
  // and the row defaulted to shown — so the palette was permanently narrowed to
  // one scope, and the only route back to everything was to hide the filter
  // controls via the funnel icon. That made the control that REVEALS filtering
  // also the control that forces it on, and left no "clear" affordance at all.
  { value: 'all', label: 'All' },
  { value: 'workspace', label: 'Workspace' },
  // Chats sits next to Workspace because both answer "find my stuff" — the
  // three that follow answer "do something". It is its own scope rather than an
  // entity type because chat history is not workspace content: it is per-user,
  // lives in a different store, and is never shared with colleagues.
  { value: 'chats', label: 'Chats' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'settings', label: 'Settings' },
  { value: 'help', label: 'Help' },
];

/** Scopes that render workspace content. */
const CONTENT_SCOPES: ReadonlySet<SearchScope> = new Set<SearchScope>(['workspace']);

/**
 * Whether the content zone is on screen. ONE predicate for two questions that
 * must never diverge: whether to narrow content by type, and whether a content
 * result count means anything.
 */
export function scopeShowsContent(scope: SearchScope, showAllResultZones = false): boolean {
  return showAllResultZones || scope === 'all' || CONTENT_SCOPES.has(scope);
}

/** Whether every zone is on screen — the explicit "All" scope, or no filter row. */
export function showsEveryZone(scope: SearchScope, showAllResultZones = false): boolean {
  return showAllResultZones || scope === 'all';
}

/** Derived from ENTITY_ICONS — `searchable: true` opts a type in. */
export const TYPE_OPTIONS = (
  Object.entries(ENTITY_ICONS) as [ThemeEntityType, (typeof ENTITY_ICONS)[ThemeEntityType]][]
)
  .filter(([, config]) => config.searchable)
  .map(([type, config]) => ({ type: type as EntityType, label: config.pluralName }));

/**
 * One pill look for every scope. A selected filter HIDES results, so it has to
 * be unmistakable: the 2026-09 design inverts the pill (ink fill, paper text),
 * the strongest signal available without spending the accent, which is kept
 * for AI and focus moments. It replaced `bg.subtle` on `bg.surface`, a few
 * percent of luminance in dark mode that read as "nothing is selected".
 *
 * The focus ring is inset (the default outset ring is clipped by the row's
 * `overflowX`), and switches color with the fill: `border.focus` is chosen
 * against the app's surfaces, not against an inverted pill, where it falls
 * under 3:1 in dark mode. `chip.selected.fg` clears its own fill by
 * construction.
 */
function pillProps(isActive: boolean) {
  return {
    size: 'sm',
    variant: 'ghost',
    fontSize: 'xs',
    h: '28px',
    flexShrink: 0,
    color: isActive ? 'chip.selected.fg' : 'text.secondary',
    fontWeight: 'medium',
    bg: isActive ? 'chip.selected.bg' : 'bg.surface',
    borderWidth: '1px',
    borderColor: isActive ? 'chip.selected.bg' : 'border.default',
    _hover: {
      bg: isActive ? 'chip.selected.bg' : 'bg.hover',
      borderColor: isActive ? 'chip.selected.bg' : 'border.hover',
    },
    _focusVisible: {
      boxShadow: `inset 0 0 0 var(--chakra-borderWidths-interactive) var(--chakra-colors-${
        isActive ? 'chip-selected-fg' : 'border-focus'
      })`,
    },
  } as const;
}

/** What the Workspace chip says: the scope, then what narrows it. */
export function workspaceChipLabel(
  activeTypes: readonly EntityType[],
  options: ReadonlyArray<{ type: EntityType; label: string }>,
): string {
  if (activeTypes.length === 0) return 'Workspace';
  if (activeTypes.length === 1) {
    const only = options.find((o) => o.type === activeTypes[0]);
    return `Workspace · ${only?.label ?? '1 type'}`;
  }
  return `Workspace · ${activeTypes.length} types`;
}

interface SearchScopeFilterProps {
  /**
   * Whether command zones exist to filter to. FALSE in the composer's `@`
   * entity picker, which renders content only — there,
   * Workspace remains as the selector that owns the Types menu, while the
   * command-only scopes are omitted.
   */
  readonly hasCommands: boolean;
  readonly scope: SearchScope;
  readonly onScopeChange: (scope: SearchScope) => void;
  readonly activeTypes: readonly EntityType[];
  readonly onTypesChange: (types: EntityType[]) => void;
}

export function SearchScopeFilter({
  hasCommands,
  scope,
  onScopeChange,
  activeTypes,
  onTypesChange,
}: SearchScopeFilterProps) {
  // A spreadsheet type option in a workspace without spreadsheets would
  // always filter to nothing, the same reason the Artifacts page gates it.
  const spreadsheetsEnabled = useSpreadsheetsEnabled();
  const typeOptions = TYPE_OPTIONS.filter(
    ({ type }) => type !== 'spreadsheet' || spreadsheetsEnabled,
  );

  // The row scrolls when the labels don't fit, so the SELECTED tab can end up
  // off screen — which reads as "my click did nothing".
  const activeTabRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [scope]);

  // Looked up BY VALUE, not by position. This was a positional destructure
  // (`const [workspaceScope, ...rest] = SCOPES`), so adding "All" to the front
  // silently relabelled the Workspace types dropdown as "All" and demoted
  // Workspace to a plain button — the whole row rearranged from a one-line
  // change. Explicit lookups make the order of SCOPES a display concern only.
  const allScope = SCOPES.find((s) => s.value === 'all');
  const commandScopes = SCOPES.filter((s) => s.value !== 'all' && s.value !== 'workspace');

  const renderScopeButton = ({ value, label }: (typeof SCOPES)[number]) => {
    const isActive = scope === value;
    return (
      <Button
        key={value}
        ref={isActive ? activeTabRef : undefined}
        aria-pressed={isActive}
        onClick={() => onScopeChange(value)}
        borderRadius="full"
        px={3.5}
        {...pillProps(isActive)}
      >
        {label}
      </Button>
    );
  };

  const workspaceActive = scope === 'workspace';
  const workspaceLabel = workspaceChipLabel(activeTypes, typeOptions);

  // A split pill: the label half selects Workspace with every type (clearing a
  // narrowing, so one tap is always "all my stuff"); the chevron half opens
  // the type checkboxes.
  const workspaceChip = (
    <HStack spacing={0} flexShrink={0}>
      <Button
        ref={workspaceActive ? activeTabRef : undefined}
        aria-pressed={workspaceActive}
        onClick={() => {
          onScopeChange('workspace');
          if (activeTypes.length > 0) onTypesChange([]);
        }}
        borderLeftRadius="full"
        borderRightRadius={0}
        pl={3.5}
        pr={2.5}
        {...pillProps(workspaceActive)}
        // After the spread, which sets all four widths: the chevron draws the
        // seam, so one hairline, not two.
        borderRightWidth={0}
      >
        {workspaceLabel}
      </Button>
      <Menu
        isLazy
        closeOnSelect={false}
        placement="bottom-start"
        onOpen={() => onScopeChange('workspace')}
      >
        <MenuButton
          as={IconButton}
          aria-label={`Workspace types: ${activeTypes.length === 0 ? 'all types' : workspaceLabel.replace('Workspace · ', '')}`}
          icon={<MaterialIcon name="expand_more" boxSize={4} color="inherit" />}
          borderLeftRadius={0}
          borderRightRadius="full"
          minW="28px"
          pl={1}
          pr={1.5}
          {...pillProps(workspaceActive)}
          // A hairline between the halves, so the chevron reads as its own
          // control. On the inverted pill it uses the pill's own text color.
          borderLeftWidth="1px"
          borderLeftColor={workspaceActive ? 'chip.selected.fg' : 'border.default'}
          _hover={{
            ...pillProps(workspaceActive)._hover,
            borderLeftColor: workspaceActive ? 'chip.selected.fg' : 'border.default',
          }}
        />
        {/* NOT portalled: the Cmd-K modal's focus lock closes a portalled
            menu on the click that opened it. */}
        <MenuList minW="200px" py={1} borderColor="border.muted" fontSize="sm">
          {/* Its own checkbox group: checked when nothing narrows the scope,
              and choosing it clears every type. */}
          <MenuOptionGroup
            type="checkbox"
            value={activeTypes.length === 0 ? ['all'] : []}
            onChange={() => onTypesChange([])}
          >
            <MenuItemOption value="all">All types</MenuItemOption>
          </MenuOptionGroup>
          <MenuDivider my={1} />
          <MenuOptionGroup
            type="checkbox"
            value={activeTypes as string[]}
            onChange={(next) =>
              onTypesChange((Array.isArray(next) ? next : [next]) as EntityType[])
            }
          >
            {typeOptions.map(({ type, label }) => (
              <MenuItemOption key={type} value={type}>
                {label}
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>
    </HStack>
  );

  // No bottom rule on this row. The 2026-09 sheet separates the tabs from the
  // results with the "RECENT" eyebrow's own hairline, one row lower — two rules
  // that close together read as a stray line rather than as structure.
  //
  // Equal padding above and below. The row had `pb` only, so the pills sat
  // hard against the top bar's rule with 16px of air beneath them — the band
  // read as belonging to the divider rather than as its own row, which is what
  // an uneven gap around a floating element always reads as.
  return (
    <HStack px={6} pt={4} pb={4} spacing={2}>
      {/* Scrolls rather than squeezing: the scopes + the Types menu don't fit
          a narrow palette, and shrinking clipped the last tab to "He". */}
      {/* `group` + `aria-pressed`, NOT `tablist`/`tab`. The ARIA tabs contract
          is a package deal — roving tabindex, Arrow/Home/End, `aria-controls`
          pointing at a `tabpanel` — and none of it is implemented here. Worse,
          a screen reader announcing "tab, 1 of 5" invites Arrow keys, which
          bubble to the cmdk root and move the RESULT highlight instead. These
          are toggle buttons that filter a list, so say that. */}
      <HStack
        spacing={1}
        role="group"
        aria-label="Filter results by kind"
        flex="1 1 auto"
        minW={0}
        overflowX="auto"
        sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        {/* "All" clears every narrowing in one click. Only meaningful when
            there ARE other scopes to clear — the legacy content-only palette
            has nothing but Workspace, so the chip would be a no-op there. */}
        {hasCommands && allScope && renderScopeButton(allScope)}
        {/* Workspace: a chip like the others, plus its type menu. */}
        {workspaceChip}
        {hasCommands && commandScopes.map(renderScopeButton)}
      </HStack>
    </HStack>
  );
}
